<template>
  <div class="flex items-center">
    <span>{{ plainText }} {{ !hasLinks ? linkText : '' }}</span>
    &nbsp;
    <base-popover
      v-if="hasLinks"
      popover-type="tooltip"
      placement="bottom-end"
      :content-style="contentStyle"
    >
      <template #trigger>
        <div class="flex gap-2.5 popover-title items-center">
          {{ linkText }}
          <svg-icon class="popover-title-icon" name="receiving_dropdown" />
        </div>
      </template>

      <template #content>
        <div class="content-popover">
          <ul class="content-popover__list">
            <template
              v-for="(section, sectionIndex) in normalizedLinks"
              :key="sectionIndex"
            >
              <li
                v-if="section.sectionTitle"
                class="content-popover__section-title"
              >
                {{ $t(section.sectionTitle) }}
              </li>
              <li
                v-for="(item, itemIndex) in section.items"
                :key="`${sectionIndex}-${itemIndex}`"
                class="content-popover__item"
                @click="item.action && item.action()"
              >
                <svg-icon
                  class="content-popover__item__icon text-brand"
                  :name="item.icon || item.iconName"
                />
                <div class="content-popover__item__content">
                  <div class="content-popover__item__title">
                    {{ $t(item.title) }}
                  </div>
                  <div
                    v-if="item.subtitle || item.desc"
                    class="content-popover__item__subtitle"
                  >
                    {{ $t(item.subtitle || item.desc) }}
                  </div>
                </div>

                <div
                  v-if="item.rightIcons || item.coinIcons"
                  class="content-popover__item__right-icons"
                >
                  <template v-if="item.rightIcons">
                    <div class="icons-stack">
                      <svg-icon
                        v-for="(iconName, idx) in item.rightIcons"
                        :key="idx"
                        :name="iconName"
                        class="icon-stacked"
                        :style="{ zIndex: item.rightIcons.length - idx }"
                      />
                    </div>
                  </template>

                  <template v-if="item.coinIcons">
                    <div class="coins-stack">
                      <div
                        v-for="(coin, idx) in displayedCoins(item.coinIcons)"
                        :key="idx"
                        class="coin-icon"
                        :style="{
                          marginLeft: idx > 0 ? '-12px' : '0',
                          zIndex: idx + 1,
                        }"
                      >
                        <img
                          :src="coin.media_url"
                          :alt="coin.title"
                          class="coin-img"
                        />
                      </div>
                      <div
                        v-if="remainingCoinsCount(item.coinIcons) > 0"
                        class="coins-count"
                        :style="{ marginLeft: '-12px', zIndex: 100 }"
                      >
                        {{ remainingCoinsCount(item.coinIcons) }}
                      </div>
                    </div>
                  </template>
                </div>
              </li>
            </template>
          </ul>
        </div>
      </template>
    </base-popover>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { BasePopover } from '@emcd-shared/base-popover'
import SvgIcon from '@/shared/ui/SvgIcon/SvgIcon.vue'
import { APP_GETTERS } from '@/shared/constants/store-constants/app'

const props = defineProps({
  plainText: {
    type: [String, Object],
    default: '',
  },
  linkText: {
    type: [String, Object],
    default: '',
  },
  popoverTitle: {
    type: [String, Object],
    default: '',
  },
  links: {
    type: Array,
    default: () => [],
  },
})

const store = useStore()
const { t } = useI18n()

const storeCoins = computed(() => store.getters[APP_GETTERS.coins])

const contentStyle = {
  padding: '8px',
  minWidth: '240px',
  maxWidth: '400px',
  width: 'auto',
}

const hasLinks = computed(() => props.links.length > 0)

const normalizedLinks = computed(() => {
  if (!props.links.length) return []

  if (props.links[0]?.items) {
    return props.links
  }

  return [
    {
      sectionTitle: props.popoverTitle,
      items: props.links,
    },
  ]
})

function displayedCoins(coinIcons) {
  if (!coinIcons) return []
  return storeCoins.value
    .filter((coin) => coinIcons.includes(coin.id))
    .slice(0, 2)
}

function remainingCoinsCount(coinIcons) {
  if (!coinIcons) return 0
  const total = storeCoins.value.filter((coin) =>
    coinIcons.includes(coin.id)
  ).length
  return total > 2 ? total - 2 : 0
}
</script>

<style scoped lang="scss">
.popover-title:hover {
  cursor: pointer;
  color: var(--brand);
}

.popover-title-icon {
  width: 24px;
  height: 24px;
}

.content-popover {
  position: relative;
}

.content-popover__list {
  background: var(--bg-3);
  width: 100%;
  min-width: 240px;
  max-width: 400px;
}

.content-popover__section-title {
  padding: 6px 12px;
  color: var(--color-3-text, #a3a3a3);
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  text-align: start;
}

.content-popover__item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: var(--radius-xs);
  transition: background 0.25s;
  width: 100%;

  &:hover {
    background: var(--bg-35);
    cursor: pointer;
  }
}

.content-popover__item__content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.content-popover__item__title {
  color: var(--color-1-text, #fafafa);
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: start;
}

.content-popover__item__subtitle {
  color: var(--color-3-text, #a3a3a3);
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: start;
}

.content-popover__item__icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  min-width: 24px;
}

.content-popover__item__right-icons {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.icons-stack {
  display: flex;
  align-items: center;
  position: relative;
}

.icon-stacked {
  width: 28px;
  height: 28px;
  flex-shrink: 0;

  &:not(:first-child) {
    margin-left: -8px;
  }
}

.coins-stack {
  display: flex;
  align-items: center;
  position: relative;
}

.coin-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid var(--color-3-back, #1a1a1a);
  flex-shrink: 0;
  overflow: hidden;
}

.coin-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.coins-count {
  display: flex;
  width: 28px;
  height: 28px;
  padding: 5px 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  aspect-ratio: 1/1;
  border-radius: 100000px;
  border: 0.5px solid var(--color-4-back, #2d2d2d);
  background: var(--color-4-back, #2d2d2d);
  color: var(--color-1-text, #fafafa);
  font-size: 12px;
  font-weight: 500;
  line-height: 150%;
  flex-shrink: 0;
}
</style>
